<template>
  <div class="Chart-box">
    <div class="close" @click="close">
      <svg viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2134" width="35" height="35">
        <path
          d="M563.8 512l262.5-312.9c4.4-5.2 0.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9c-4.4 5.2-0.7 13.1 6.1 13.1h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z"
          p-id="2135" fill="#000000"></path>
      </svg>
    </div>
    <h3 class="title">{{ props.dataObj.data.metricName }}</h3>
    <div class="Chart" ref="chart" @mousedown.stop></div>
  </div>
</template>

<script setup>
import { defineComponent, watch, reactive, ref, computed, onMounted,nextTick } from 'vue'
import * as echarts from 'echarts'
import { useFrameDataStore } from "@/store/frameDataStore";
const frameDataStore = useFrameDataStore();

const props = defineProps(['dataObj'])
const emit = defineEmits(['close'])
const chart = ref()
let myChartObj = null;
const close = () => {
  emit('close')
}

onMounted(() => {
  if (props.dataObj.checkbox) {
    nextTick(() => {
      initChart(props.dataObj.data);
    });
  }
});

watch(() => frameDataStore.currentTime, (e) => {
  if (!props.dataObj.checkbox) return;
  const data_local = frameDataStore.dataListStore;
  const data_local_filter = data_local
    .filter((it) => it.timestamp <= e)
    .sort((a, b) => a.timestamp - b.timestamp)
    .map((el) => {
      let it = el.metricDataFrameList.find((e) => e.metricName === props.dataObj.data.metricName)
      return {
        metricLineDataList: it?it.metricLineDataList:[]
      };
    })
  const res = buildData(props.dataObj.data, data_local_filter)
  refleshChartData(props.dataObj.data, res);
})

watch(() => props.dataObj, (dataObj) => {
  if (dataObj.checkbox) {
    initChart(dataObj.data);
  }
},
{
  immediate: true,
  deep: true
})

function initChart(setting) {
  const chartDom = chart.value;
  if (!chartDom) return;
  if (!myChartObj) { myChartObj = echarts.init(chartDom); }
  const resizeObserver = new ResizeObserver((entries) => {
    entries.forEach(() => {
      if (myChartObj) {
        myChartObj.resize()
      }
    })
  })
  resizeObserver.observe(chartDom);
  initChartOption(setting);
}
function initChartOption(setting){
  if(!setting.metricLineDataList.length)return;
  let seriesData = [];
  let legendData = [];

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      },
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      position: function (pos, params, el, elRect, size) {
        var obj = { top: 10 };
        obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 30;
        return obj;
      },
    },
    legend: {
      right: 100,
      top: 10,
      orient: 'horizontal',
      borderColor: 'rgba(0,0,0,.2)',
      itemStyle: {
        opacity: 0.3
      },
      lineStyle: {
        opacity: 0.3
      },
      backgroundColor: 'rgba(255,255,255,.2)',
      borderWidth: 1,
      data:legendData
    },
    grid: {
      left: '6%',
      right: '14%',
      bottom: '14%',
      top: '15%',
      containLabel: true
    },
    dataZoom: [
      {
        type: 'slider',
        show: true,
        xAxisIndex: [0],
        animation: false,
        start: 0,
        end: 100
      },
      {
        type: 'inside',
        xAxisIndex: [0],
        animation: false,
        start: 0,
        end: 100
      },
    ],
    xAxis: {
      type: 'category',
      axisLabel: {
        show: true,
        showMaxLabel: true,
      },
      axisTick: {
        inside: true
      },
      minorTick: {
        show: true,
        splitNumber: 5
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed'
        }
      }
    },
    series: seriesData,
    animation: false,
    yAxis: {
      type: 'value',
      minorTick: {
        show: true
      },
      axisTick: {
        inside: true
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed'
        }
      },
      axisLabel: {
        formatter: (value, index) => {
          return String(value).indexOf('.') !== -1 ? value.toFixed(2) : value
        }
      },
      axisLine: {
        show: true
      }
    },
    toolbox: {
      feature: {
        dataZoom: {},
        restore: {}
      }
    }
  }

  setting.metricLineDataList.forEach((item) => {
    if (item.metricLineType == 4) {
      option.xAxis.axisLabel.rotate = 25
      option.xAxis.axisLabel.fontSize = 12
    }
    let seriesItem = {
      name:item.metricLineName,
      type:'line',
      symbol: 'none',
      data:[]
    }
    seriesData.push(seriesItem);
    legendData.push(item.metricLineName);
  })
  myChartObj.setOption(option);
}
function refleshChartData(setting, data) {
  if (!myChartObj) {
    initChart(setting);
  };
  const opts = {
    series: data,
    xAxis: { type: 'category' },
  }
  myChartObj.setOption(opts)
}

function addZero(num) {
  return num < 10 ? '0' + num : num
}
function addTwoZero(num) {
  return num < 10 ? '00' + num : num < 100 ? '0' + num : num
}
function formatTime(times) {
  const date = new Date(times)
  const hh = date.getHours()
  const mm = date.getMinutes()
  const ss = date.getSeconds()
  const ms = date.getMilliseconds()
  return `${addZero(hh)}:${addZero(mm)}:${addZero(ss)}.${addTwoZero(ms)}`
}

function buildData(setting, dataList) {
  let seriesData = [];
  let seriesDataObj = {};
  setting.metricLineDataList.forEach((item) => {
    seriesDataObj[item.metricLineName] = [];
  })

  dataList.forEach((dataItem) => {
    if (dataItem.metricLineDataList.length) {
      setting.metricLineDataList.forEach((chartLine) => {
        let item = dataItem.metricLineDataList.find(it => it.metricLineName === chartLine.metricLineName);
        if (item.metricLineType == 4) {
          let obj = item.metricPointDataList[0];
          let objX = formatTime(obj.x)
          if (!item.isFormat && !objX.includes('NaN')) {
            obj.x = objX
            item.isFormat = true;
          }
          seriesDataObj[item.metricLineName].push([obj.x, obj.y])
        } else {
          seriesDataObj[item.metricLineName] = item.metricPointDataList.map((v) => {
            let obj = v.metricDataFrameList[0];
            let objX = formatTime(obj.x)
            if (!item.isFormat && !objX.includes('NaN')) {
              obj.x = objX
              item.isFormat = true;
            }
            return [v.x, v.y]
          })
        }
      })
    }
  })

  
  setting.metricLineDataList.forEach((item) => {
    if (item.metricLineType === 3) {
      if (seriesDataObj[item.metricLineName][0] == undefined 
      || seriesDataObj[item.metricLineName][2] == undefined) return

      seriesData.push({
        name: item.name,
        markArea: {
          silent: true,
          data: [
            [
              {
                xAxis: seriesDataObj[item.metricLineName][0][0],
                yAxis: seriesDataObj[item.metricLineName][0][1],
                itemStyle: {
                  color: 'rgba(255,255,255,0)'
                },
                label: {
                  show: true,
                  position: 'inside',
                  color: 'black',
                  fontSize: 10,
                  formatter: item.metricLineName
                }
              },
              {
                xAxis: seriesDataObj[item.metricLineName][2][0],
                yAxis: seriesDataObj[item.metricLineName][2][1]
              }
            ]
          ]
        }
      })
    } else {
      seriesData.push({
        name: item.metricLineName,
        data: seriesDataObj[item.metricLineName],
      })
    }
  })
  return seriesData;
}
</script>

<style lang="less" scoped>
.Chart {
  width: 100%;
  height: 100%;
}

.Chart-box {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 10px;
  background-color: #fff;
  margin: 0 auto;
  box-sizing: border-box;
  box-shadow: 0 0 16px #ccc;

  .close {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 22;
    cursor: pointer;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    // background: rgba(255, 255, 255, 0.6);
    border-radius: 5px;

    svg {
      width: 20px;
    }
  }

  .Chart {
    // pointer-events: none;
    padding: 0 10px;
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    border: 1px solid #ccc;
    box-shadow: 0 0 8px #ccc;
    // border-radius: 50px;
    // background: rgba(255, 255, 255, 0.6);
    background: #fff;
  }
}

.title {
  text-align: center;
  color: #fff;
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 18px;
  padding: 0 10px 10px;
  background-color: #000;
}
</style>